/**
 * What a NumberInput accepts and what it hands its consumer.
 *
 * An Arabic keyboard types Arabic-Indic digits (٠–٩) and a Persian one the
 * extended set (۰–۹); a seller typing her price as ٣٥٠ must not be left with an
 * empty field and the browser's "Please fill out this field" (roundtable 2
 * QA). Both sets, and the Arabic decimal separator "٫", are folded to ASCII
 * for the consumer; the field keeps showing what she typed.
 */
const ARABIC_INDIC_ZERO = 0x0660;
const PERSIAN_ZERO = 0x06f0;

export function toAsciiNumber(raw: string): string {
  let out = "";
  for (const ch of raw) {
    const code = ch.codePointAt(0)!;
    if (code >= ARABIC_INDIC_ZERO && code <= ARABIC_INDIC_ZERO + 9) out += String(code - ARABIC_INDIC_ZERO);
    else if (code >= PERSIAN_ZERO && code <= PERSIAN_ZERO + 9) out += String(code - PERSIAN_ZERO);
    else if (ch === "٫") out += ".";
    else out += ch;
  }
  return out;
}

/** True when `raw` (any digit script) is a number in progress the field accepts. */
export function isAcceptedNumberInput(raw: string, allowDecimal: boolean): boolean {
  const ascii = toAsciiNumber(raw);
  if (ascii === "" || ascii === "-") return true;
  if (allowDecimal && (ascii === "." || ascii === "-.")) return true;
  return (allowDecimal ? /^-?[0-9]*\.?[0-9]*$/ : /^-?[0-9]*$/).test(ascii);
}

/** Which digits a field shows a value it did not see typed in. */
export type NumberDigits = "latn" | "arab";

/**
 * ASCII number text in the given digits: "680" → "٦٨٠", "12.5" → "١٢٫٥".
 * The minus stays as it is. `latn` returns the text unchanged.
 */
export function toLocaleDigits(ascii: string, digits: NumberDigits = "latn"): string {
  if (digits !== "arab") return ascii;
  let out = "";
  for (const ch of ascii) {
    if (ch >= "0" && ch <= "9") out += String.fromCodePoint(ARABIC_INDIC_ZERO + Number(ch));
    else if (ch === ".") out += "٫";
    else out += ch;
  }
  return out;
}

/**
 * The text the field shows: what was typed while it still means the value the
 * consumer holds, else the value itself (a reset, a prefill, a programmatic set)
 * — in the portal's digits, so an Arabic page shows a saved price as "٦٨٠".
 */
export function displayedNumber(
  typed: string | null,
  value: string,
  digits: NumberDigits = "latn",
): string {
  return typed !== null && toAsciiNumber(typed) === value
    ? typed
    : toLocaleDigits(value, digits);
}
